import Long from 'long';
import browserStorage from './browserStorage';

export function getCacheData<T = any>(dataSetName: string, expiration: number): T | null {
  const cacheDataStr = browserStorage.getItem(dataSetName);
  if (cacheDataStr) {
    try {
      const cacheData = JSON.parse(cacheDataStr, (key, value) => {
        if (typeof value === 'string' && new RegExp('^[0-9]{9,80}$').test(value)) {
          return Long.fromString(value);
        }
        return value;
      });
      const cacheDataStamp: number = (cacheData && cacheData.stamp) || 0;
      const stampDiff = new Date().getTime() - cacheDataStamp;
      if (cacheData.data && expiration && cacheDataStamp && stampDiff < expiration) {
        return cacheData.data as T;
      }
    } catch (e) {
      console.log(e);
    }
  }
  return null;
}

export function setCacheData(dataSetName: string, data: any) {
  if (dataSetName) {
    try {
      const cacheData = {
        data,
        stamp: new Date().getTime(),
      };

      const cacheDataStr = JSON.stringify(cacheData);
      browserStorage.setItem(dataSetName, cacheDataStr);
    } catch (e) {
      console.log(e);
    }
  }
}


export enum CacheType {
  Text,
  Blob,
  Json,
  ArrayBuffer,
}

type AnyLiteral = Record<string, any>;

export async function fetch(
  cacheName: string, key: string, type?: CacheType, isHtmlAllowed = false,
) {
  if (!caches) {
    return undefined;
  }

  try {
    // To avoid the error "Request scheme 'webdocument' is unsupported"
    const request = new Request(key.replace(/:/g, '_'));
    const cache = await caches.open(cacheName);
    const response = await cache.match(request);
    if (!response) {
      return undefined;
    }

    const contentType = response.headers.get('Content-Type');

    switch (type) {
      case CacheType.Text:
        return await response.text();
      case CacheType.Blob: {
        const blob = await response.blob();
        const shouldRecreate = !blob.type || (!isHtmlAllowed && blob.type.includes('html'));
        // iOS Safari fails to preserve `type` in cache
        const resolvedType = blob.type || contentType;

        if (!(shouldRecreate && resolvedType)) {
          return blob;
        }

        return new Blob([blob], {type: resolvedType});
      }
      case CacheType.Json:
        return await response.json();
      case CacheType.ArrayBuffer:
        return await response.arrayBuffer();
      default:
        return undefined;
    }
  } catch (err: any) {
    console.debug(`%c cache->fetch ${key} from ${cacheName} type=${type} error:`, 'color: gray', err?.message);
    return undefined;
  }
}

export async function put(cacheName: string, key: string, data: AnyLiteral | Blob | ArrayBuffer | string) {
  if (!caches) {
    return undefined;
  }

  try {
    const cacheData = typeof data === 'string' || data instanceof Blob || data instanceof ArrayBuffer
      ? data
      : JSON.stringify(data);
    // To avoid the error "Request scheme 'webdocument' is unsupported"
    const request = new Request(key.replace(/:/g, '_'));
    const response = new Response(cacheData);
    const cache = await caches.open(cacheName);
    return await cache.put(request, response);
  } catch (err: any) {
    console.debug(`%c cache->put ${key} to ${cacheName} error:`, 'color: gray', err?.message);
    return undefined;
  }
}

export async function remove(cacheName: string, key: string) {
  try {
    if (!caches) {
      return undefined;
    }

    const cache = await caches.open(cacheName);
    return await cache.delete(key);
  } catch (err: any) {
    console.debug(`%c cache->remove ${key} from ${cacheName} error:`, 'color: gray', err?.message);
    return undefined;
  }
}

export async function clear(cacheName: string) {
  try {
    if (!caches) {
      return undefined;
    }

    return await caches.delete(cacheName);
  } catch (err: any) {
    console.debug(`%c cache->clear ${cacheName} error:`, 'color: gray', err?.message);
    return undefined;
  }
}
